import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import {
  BonusCampaignAvailabilityPeriod,
  BonusCampaignExcludeType,
  BonusLabel,
} from 'api/Wallet/Types/BonusCampaignTypes'
import { PaymentProviderDto } from 'api/Wallet/Types/PaymentProviderDto'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { WalletStates } from 'api/Wallet/Types/WalletStates'
import { WalletTypeDto } from 'api/Wallet/Types/WalletTypeDto'

import { NameDto } from '../../api/Common/Types'
import { AppState } from '../index'

export interface WalletState {
  states: WalletStates[]
  restrictions: NameDto[]
  currencies: NameDto<CurrencyType>[]
  clientWalletTypes: WalletTypeDto[]
  walletType: WalletTypeDto[]
  paymentProviders: PaymentProviderDto[]
  socialMediaTypes: NameDto[]
  bonusTypes: NameDto[]
  walletByTickmillCompanyId: Record<TickmillCompanyTypes, WalletCurrencyDto[]>
  bonusLabels: BonusLabel[]
  bonusCampaignExcludeTypes: BonusCampaignExcludeType[]
  bonusCampaignAvailabilityPeriods: BonusCampaignAvailabilityPeriod[]
  bonusCampaignCountdownTriggers: BonusCampaignAvailabilityPeriod[]
}

const initialState: WalletState = {
  states: [],
  restrictions: [],
  currencies: [],
  clientWalletTypes: [],
  walletType: [],
  paymentProviders: [],
  socialMediaTypes: [],
  bonusTypes: [],
  walletByTickmillCompanyId: createWalletByTickmillCompanyId([]),
  bonusLabels: [],
  bonusCampaignExcludeTypes: [],
  bonusCampaignAvailabilityPeriods: [],
  bonusCampaignCountdownTriggers: [],
}

export const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    setWalletStates: (state: WalletState, action: PayloadAction<WalletStates[]>) => {
      state.states = action.payload
    },
    setWalletRestrictions: (state: WalletState, action: PayloadAction<NameDto[]>) => {
      state.restrictions = action.payload
    },
    setWalletCurrencies: (state: WalletState, action: PayloadAction<WalletCurrencyDto[]>) => {
      state.currencies = action.payload
    },
    setWalletTypes: (state: WalletState, action: PayloadAction<WalletTypeDto[]>) => {
      state.walletType = action.payload
    },
    setClientWalletTypes: (state: WalletState, action: PayloadAction<WalletTypeDto[]>) => {
      state.clientWalletTypes = action.payload
    },
    setWalletPaymentProviders: (
      state: WalletState,
      action: PayloadAction<PaymentProviderDto[]>
    ) => {
      state.paymentProviders = action.payload
    },
    setSocialMediaTypes: (state: WalletState, action: PayloadAction<NameDto[]>) => {
      state.socialMediaTypes = action.payload
    },
    setBonusTypes: (state: WalletState, action: PayloadAction<NameDto[]>) => {
      state.bonusTypes = action.payload
    },
    setWalletCurrenciesByCompany: (
      state: WalletState,
      action: PayloadAction<Record<number, WalletCurrencyDto[]>>
    ) => {
      state.walletByTickmillCompanyId = action.payload
    },
    setBonusLabels: (state: WalletState, action: PayloadAction<BonusLabel[]>) => {
      state.bonusLabels = action.payload
    },
    setBonusCampaignExcludeTypes: (
      state: WalletState,
      action: PayloadAction<BonusCampaignExcludeType[]>
    ) => {
      state.bonusCampaignExcludeTypes = action.payload
    },
    setBonusCampaignAvailabinilityPeriods: (
      state: WalletState,
      action: PayloadAction<BonusCampaignAvailabilityPeriod[]>
    ) => {
      state.bonusCampaignAvailabilityPeriods = action.payload
    },
    setBonusCampaignCountdownTriggers: (
      state: WalletState,
      action: PayloadAction<BonusCampaignAvailabilityPeriod[]>
    ) => {
      state.bonusCampaignCountdownTriggers = action.payload
    },
  },
})

export const selectWalletStates = (state: AppState) => state.wallet.states
export const selectWalletRestrictions = (state: AppState) => state.wallet.restrictions
export const selectWalletCurrencies = (state: AppState) => state.wallet.currencies
export const selectClientWalletTypes = (state: AppState) => state.wallet.clientWalletTypes
export const selectWalletTypes = (state: AppState) => state.wallet.walletType
export const selectWalletPaymentProvider = (state: AppState) => state.wallet.paymentProviders
export const selectSocialMediaTypes = (state: AppState) => state.wallet.socialMediaTypes
export const selectBonusTypes = (state: AppState) => state.wallet.bonusTypes
export const selectCompanyWalletCurrencies = (state: AppState) =>
  state.wallet.walletByTickmillCompanyId

export const selectBonusLabels = (state: AppState) => state.wallet.bonusLabels
export const selectBonusCampaignExcludeTypes = (state: AppState) =>
  state.wallet.bonusCampaignExcludeTypes

export const selectBonusCampaignAvailabilityPeriod = (state: AppState) =>
  state.wallet.bonusCampaignAvailabilityPeriods

export const selectBonusCampaignCountdownTriggers = (state: AppState) =>
  state.wallet.bonusCampaignCountdownTriggers

function createWalletByTickmillCompanyId<T>(defaultValue: T): Record<TickmillCompanyTypes, T> {
  return Object.values(TickmillCompanyTypes).reduce(
    (acc, curr) => {
      acc[curr as TickmillCompanyTypes] = defaultValue
      return acc
    },
    {} as Record<TickmillCompanyTypes, T>
  )
}
